import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectBuildingCard } from 'shared/selectors/buildingCard';
import { selectBuildingOffers } from 'shared/selectors/buildingOffers';
import { subscribeToBuildingOffers } from 'shared/services/subscribeToBuildingOffers';
import { selectOffer, setOffersFilters, setOffersSort } from 'shared/slices/buildingOffers';
import { fetchBuildingOffers } from 'shared/thunks/buildingOffers';
import type { TDispatch } from 'shared/types/redux';
import { useApplicationContext } from 'shared/utils/applicationContext';
import {
  changeOffersDealType,
  changeOffersPropertyType,
  findOfferCategory,
  getAvailablePropertyTypes,
  getOffersEmptyTitle,
  getOffersListingUrl,
  getOffersMoreLinkText,
} from 'shared/utils/buildingOffers';

import { BuildingOffers, OffersContent, OffersError, OffersSkeleton } from './BuildingOffers';
import { OfferPreview } from './components/OfferPreview';
import { OffersFilters } from './components/OffersFilters';
import { OffersSubscription } from './components/OffersSubscription';
import { OffersTable } from './components/OffersTable';
import { useIsNearViewport, useIsSlowLoading } from './hooks';

const BuildingOffersBlock = () => {
  const card = useSelector(selectBuildingCard);
  const { filters, sort, status, data, selectedIndex } = useSelector(selectBuildingOffers);
  const dispatch = useDispatch<TDispatch>();
  const { logger } = useApplicationContext();
  const sectionRef = useRef<HTMLElement>(null);
  const isNearViewport = useIsNearViewport(sectionRef);
  const isSlowLoading = useIsSlowLoading(status === 'loading');
  const [arrowTop, setArrowTop] = useState<number | null>(null);
  const { offerCategories: categories } = card;
  const { dealType, propertyType, isFromRepresentative } = filters;

  // Как LazyLoad монолита: первый запрос — когда блок подошёл к экрану, дальше — на каждую смену выборки
  useEffect(() => {
    if (isNearViewport && propertyType) {
      dispatch(fetchBuildingOffers());
    }
  }, [isNearViewport, filters, sort, propertyType, dispatch]);

  const handleSelectedRowMiddle = useCallback((top: number) => setArrowTop(top), []);

  const category = propertyType ? (findOfferCategory(categories, dealType, propertyType) ?? null) : null;

  const renderContent = () => {
    if (!propertyType) {
      return (
        <OffersSubscription
          title={getOffersEmptyTitle(card.type, dealType)}
          onSubscribe={email => subscribeToBuildingOffers({ logger, buildingId: card.id, dealType, email })}
        />
      );
    }

    if (status === 'error') {
      return <OffersError onRetry={() => dispatch(fetchBuildingOffers())} />;
    }

    if (!data) {
      return <OffersSkeleton />;
    }

    const selectedOffer = data.offers[selectedIndex] ?? null;
    const hasMore = data.offersCount > data.offers.length;

    return (
      <OffersContent
        isLoading={isSlowLoading}
        arrowTop={selectedOffer ? arrowTop : null}
        table={
          <OffersTable
            dealType={dealType}
            offers={data.offers}
            selectedIndex={selectedIndex}
            sort={sort}
            moreLink={
              hasMore
                ? {
                    url: getOffersListingUrl({ buildingId: card.id, filters: { ...filters, propertyType }, sort }),
                    text: getOffersMoreLinkText(data.offersCount),
                  }
                : null
            }
            onSort={value => dispatch(setOffersSort(value))}
            onSelect={index => dispatch(selectOffer(index))}
            onSelectedRowMiddle={handleSelectedRowMiddle}
          />
        }
        preview={
          selectedOffer && (
            <OfferPreview
              offer={selectedOffer}
              dealType={dealType}
              representativeName={isFromRepresentative ? (card.representative?.name ?? null) : null}
            />
          )
        }
      />
    );
  };

  return (
    <BuildingOffers
      sectionRef={sectionRef}
      filters={
        <OffersFilters
          dealType={dealType}
          propertyType={propertyType}
          availablePropertyTypes={getAvailablePropertyTypes(categories, dealType)}
          category={category}
          isFromRepresentative={isFromRepresentative}
          onDealTypeChange={value =>
            value !== dealType && dispatch(setOffersFilters(changeOffersDealType(categories, filters, value)))
          }
          onPropertyTypeChange={value =>
            value !== propertyType && dispatch(setOffersFilters(changeOffersPropertyType(categories, filters, value)))
          }
          onFromRepresentativeChange={value =>
            value !== isFromRepresentative && dispatch(setOffersFilters({ ...filters, isFromRepresentative: value }))
          }
        />
      }
    >
      {renderContent()}
    </BuildingOffers>
  );
};

/** Блок есть, только если у здания есть объявления (`{% if categoriesData %}` монолита) */
export const BuildingOffersContainer = () => {
  const { offerCategories } = useSelector(selectBuildingCard);

  return offerCategories.length ? <BuildingOffersBlock /> : null;
};
